// Firebase Authentication service
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence
} from 'firebase/auth';
import { auth } from '../config/firebase.js';
import { isIOSWebKit, getRecommendedAuthMethod, getBrowserDebugInfo } from '../utils/browserDetect.js';

class FirebaseAuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
    this.googleProvider = new GoogleAuthProvider();
    
    // Configure Google provider for better user experience and Safari compatibility
    this.googleProvider.setCustomParameters({
      prompt: 'select_account'
    });
    this.googleProvider.addScope('email');
    this.googleProvider.addScope('profile');
    
    // Set up authentication persistence with fallback for iOS Safari private mode
    this.initializePersistence();
  }

  /**
   * Initialize authentication persistence with fallbacks for iOS Safari
   */
  async initializePersistence() {
    try {
      await setPersistence(auth, browserLocalPersistence);
      console.log('✅ Browser local persistence enabled');
    } catch (error) {
      console.warn('⚠️ Local persistence failed, falling back to memory persistence:', error);
      try {
        await setPersistence(auth, inMemoryPersistence);
        console.log('✅ Memory persistence enabled as fallback');
      } catch (fallbackError) {
        console.error('❌ All persistence methods failed:', fallbackError);
      }
    }
  }

  /**
   * Detect if the current device should use redirect authentication
   * Updated to use new browser detection utility
   * @returns {boolean} True if should use redirect instead of popup
   */
  shouldUseRedirectAuth() {
    const recommendation = getRecommendedAuthMethod();
    return recommendation.method === 'redirect';
  }

  /**
   * Legacy method for backward compatibility
   * @returns {boolean} True if mobile device or popup-blocking browser
   */
  isMobileOrPopupBlocked() {
    return this.shouldUseRedirectAuth();
  }

  /**
   * Enhanced Safari on iOS detection
   * @param {string} userAgent - User agent string
   * @returns {boolean} True if Safari on iOS
   */
  isSafariOnIOS(userAgent) {
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent);
    const isNotChrome = !/CriOS/i.test(userAgent);
    const isNotFirefox = !/FxiOS/i.test(userAgent);
    const isNotEdge = !/EdgiOS/i.test(userAgent);

    return isIOS && isSafari && isNotChrome && isNotFirefox && isNotEdge;
  }

  /**
   * Get debug information about device detection
   * Updated to use new browser detection utility
   * @returns {Object} Debug information
   */
  getDeviceDebugInfo() {
    // Get enhanced browser information from the new utility
    const browserInfo = getBrowserDebugInfo();
    const authRecommendation = getRecommendedAuthMethod();
    
    // Add legacy fields for backward compatibility
    return {
      ...browserInfo,
      // Legacy compatibility fields
      isSafariMobile: browserInfo.isIOSSafari,
      isAnyIOSBrowser: browserInfo.isIOSWebKit,
      willUseRedirect: authRecommendation.method === 'redirect',
      authRecommendation
    };
  }

  /**
   * Register a new user with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {string} displayName - User's display name
   * @returns {Promise<Object>} User object or error
   */
  async register(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update the user's display name
      if (displayName) {
        await updateProfile(user, {
          displayName: displayName
        });
      }
      
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: displayName || user.displayName
        }
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  }

  /**
   * Sign in user with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} User object or error
   */
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        }
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  }

  /**
   * Handle redirect result from Google OAuth (for mobile devices)
   * Enhanced with Safari storage partitioning workaround
   * @returns {Promise<Object>} User object, null if no redirect, or error
   */
  async handleRedirectResult() {
    try {
      console.log('🔄 Checking for Google redirect result...');
      console.log('🔍 Current URL:', window.location.href);
      console.log('🔍 User Agent:', navigator.userAgent);

      // Check if we were expecting a redirect
      let wasPendingRedirect = false;
      try {
        const pendingFlag = sessionStorage.getItem('pendingGoogleRedirect');
        if (pendingFlag) {
          wasPendingRedirect = true;
          const timestamp = parseInt(pendingFlag, 10);
          const elapsed = Date.now() - timestamp;
          console.log('✅ Found pendingGoogleRedirect flag from', elapsed, 'ms ago');
          sessionStorage.removeItem('pendingGoogleRedirect');
        } else {
          console.log('ℹ️ No pendingGoogleRedirect flag found in sessionStorage');
        }
      } catch (storageError) {
        console.warn('⚠️ Could not check sessionStorage:', storageError);
      }

      // Check URL for OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthParams = urlParams.has('code') || urlParams.has('state') ||
                             window.location.hash.includes('access_token');

      console.log('🔍 URL analysis:', {
        currentUrl: window.location.href,
        hasOAuthParams,
        wasPendingRedirect,
        hasCode: urlParams.has('code'),
        hasState: urlParams.has('state'),
        hasAccessToken: window.location.hash.includes('access_token'),
        urlParams: Array.from(urlParams.entries()),
        hash: window.location.hash
      });

      // Check if we're dealing with Safari storage partitioning
      const deviceInfo = this.getDeviceDebugInfo();

      console.log('🔍 Device detection info:', {
        isSafariMobile: deviceInfo.isSafariMobile,
        isIOSSafari: deviceInfo.isIOSSafari,
        isIOSWebKit: deviceInfo.isIOSWebKit,
        platform: deviceInfo.platform,
        userAgent: deviceInfo.userAgent,
        maxTouchPoints: deviceInfo.maxTouchPoints,
        recommendedAuthMethod: deviceInfo.authRecommendation?.method
      });

      // For Safari, try multiple approaches to handle storage partitioning
      if (deviceInfo.isSafariMobile) {
        console.log('🍎 Safari mobile detected - using enhanced redirect handling');
        return await this.handleSafariRedirectResult();
      }

      // Standard redirect handling for other browsers
      console.log('🖥️ Desktop browser detected - using standard redirect handling');
      return await this.handleStandardRedirectResult();

    } catch (error) {
      console.error('❌ Google redirect result error:', error);

      // Enhanced error logging for debugging
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack,
        deviceInfo: this.getDeviceDebugInfo()
      });

      // Handle specific Safari/mobile errors
      if (error.message === 'Redirect result timeout') {
        return {
          success: false,
          error: 'Authentication timed out. Please try again.',
          hasRedirect: true,
          errorType: 'timeout'
        };
      }

      // Handle storage partitioning errors
      if (error.message && error.message.includes('sessionStorage')) {
        return {
          success: false,
          error: 'Safari storage issue detected. Please try signing in again or use a different browser.',
          hasRedirect: true,
          errorType: 'storage-partitioning',
          safariWorkaround: true
        };
      }

      return {
        success: false,
        error: this.getGoogleErrorMessage(error.code),
        hasRedirect: true,
        errorType: 'firebase',
        originalError: error.code
      };
    }
  }

  /**
   * Safari-specific redirect result handling
   * @returns {Promise<Object>} Authentication result
   */
  async handleSafariRedirectResult() {
    try {
      console.log('🍎 Starting Safari redirect result check...');
      console.log('🔍 Current auth state before getRedirectResult:', {
        hasCurrentUser: !!auth.currentUser,
        userEmail: auth.currentUser?.email,
        userUid: auth.currentUser?.uid,
        isAnonymous: auth.currentUser?.isAnonymous
      });
      console.log('🔍 Storage availability:', {
        localStorage: (() => {
          try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            return 'available';
          } catch (e) {
            return 'blocked: ' + e.message;
          }
        })(),
        sessionStorage: (() => {
          try {
            sessionStorage.setItem('test', 'test');
            sessionStorage.removeItem('test');
            return 'available';
          } catch (e) {
            return 'blocked: ' + e.message;
          }
        })()
      });

      // Shorter timeout for Safari due to storage issues
      console.log('⏳ Calling getRedirectResult(auth)...');
      const redirectPromise = getRedirectResult(auth);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Safari redirect timeout')), 8000)
      );

      console.log('⏳ Waiting for redirect result (8 second timeout)...');
      const result = await Promise.race([redirectPromise, timeoutPromise]);

      console.log('🔍 Safari redirect result received:', result);
      console.log('🔍 Result type:', typeof result);
      console.log('🔍 Result is null?', result === null);
      console.log('🔍 Result is undefined?', result === undefined);
      console.log('🔍 Result has user property?', result && 'user' in result);
      console.log('🔍 Result.user value:', result?.user);

      if (!result) {
        console.log('ℹ️ No Safari redirect result available (result is null)');
        return { success: true, user: null, hasRedirect: false };
      }

      // Check if result has a user
      if (!result.user) {
        console.warn('⚠️ Safari redirect result exists but has no user - this is unusual');
        console.log('🔍 Full result object:', JSON.stringify(result, null, 2));
        return { success: true, user: null, hasRedirect: false };
      }

      console.log('✅ Safari redirect result has user:', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: result.user?.displayName,
        isNewUser: result._tokenResponse?.isNewUser
      });

      const user = result.user;
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        isNewUser: result._tokenResponse?.isNewUser || false,
        hasRedirect: true,
        authMethod: 'safari-redirect'
      };
    } catch (safariError) {
      console.error('❌ Safari redirect handling failed:', safariError);
      console.error('❌ Error message:', safariError.message);
      console.error('❌ Error stack:', safariError.stack);

      // If Safari redirect fails, suggest popup as alternative
      if (safariError.message === 'Safari redirect timeout' ||
          safariError.message.includes('sessionStorage')) {
        console.warn('⚠️ Safari storage issue detected - redirect auth failed');
        return {
          success: false,
          error: 'Safari redirect authentication failed. Please try the popup method instead.',
          hasRedirect: true,
          errorType: 'safari-storage-issue',
          suggestPopup: true
        };
      }

      throw safariError;
    }
  }

  /**
   * Standard redirect result handling for non-Safari browsers
   * @returns {Promise<Object>} Authentication result
   */
  async handleStandardRedirectResult() {
    try {
      console.log('🖥️ Starting standard redirect result check...');
      console.log('🔍 Current auth state before getRedirectResult:', {
        hasCurrentUser: !!auth.currentUser,
        userEmail: auth.currentUser?.email,
        userUid: auth.currentUser?.uid
      });

      // Add timeout for redirect results
      const redirectPromise = getRedirectResult(auth);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Redirect result timeout')), 10000)
      );

      console.log('⏳ Waiting for redirect result (10 second timeout)...');
      const result = await Promise.race([redirectPromise, timeoutPromise]);

      console.log('🔍 Standard redirect result received:', result);
      console.log('🔍 Result type:', typeof result);
      console.log('🔍 Result is null?', result === null);
      console.log('🔍 Result is undefined?', result === undefined);
      console.log('🔍 Result has user property?', result && 'user' in result);
      console.log('🔍 Result.user value:', result?.user);

      if (!result) {
        console.log('ℹ️ No redirect result available (result is null/undefined)');
        return { success: true, user: null, hasRedirect: false };
      }

      // Check if result has a user
      if (!result.user) {
        console.warn('⚠️ Redirect result exists but has no user - this is unusual');
        console.log('🔍 Full result object:', JSON.stringify(result, null, 2));
        return { success: true, user: null, hasRedirect: false };
      }

      console.log('✅ Standard redirect result has user:', {
        uid: result.user?.uid,
        email: result.user?.email,
        displayName: result.user?.displayName,
        isNewUser: result._tokenResponse?.isNewUser
      });

      const user = result.user;
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        isNewUser: result._tokenResponse?.isNewUser || false,
        hasRedirect: true,
        authMethod: 'standard-redirect'
      };
    } catch (error) {
      console.error('❌ Error in handleStandardRedirectResult:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);

      // Provide helpful error messages for common issues
      if (error.code === 'auth/unauthorized-domain') {
        console.error('🔴 UNAUTHORIZED DOMAIN ERROR:');
        console.error('   The current domain is not authorized in Firebase Console.');
        console.error('   Go to: https://console.firebase.google.com/project/' + auth.config.projectId + '/authentication/settings');
        console.error('   Add this domain to "Authorized domains": ' + window.location.origin);
      } else if (error.code === 'auth/invalid-api-key') {
        console.error('🔴 INVALID API KEY ERROR:');
        console.error('   Check your .env file and ensure REACT_APP_FIREBASE_API_KEY is correct');
      } else if (error.code === 'auth/operation-not-allowed') {
        console.error('🔴 OPERATION NOT ALLOWED ERROR:');
        console.error('   Google Sign-In is not enabled in Firebase Console.');
        console.error('   Go to: https://console.firebase.google.com/project/' + auth.config.projectId + '/authentication/providers');
        console.error('   Enable Google as a sign-in provider');
      } else if (error.message && error.message.includes('redirect')) {
        console.error('🔴 REDIRECT ERROR:');
        console.error('   This may be caused by:');
        console.error('   1. Missing redirect URI in Google Cloud Console');
        console.error('   2. Incorrect OAuth configuration');
        console.error('   3. Browser blocking third-party cookies');
        console.error('   Verify Google Cloud Console: https://console.cloud.google.com/apis/credentials?project=' + auth.config.projectId);
      }

      // Return error result
      return {
        success: false,
        error: error.message,
        hasRedirect: true,
        errorType: 'redirect-error',
        originalError: error.code
      };
    }
  }

  /**
   * Sign in or register user with Google OAuth
   * Enhanced to use redirect-first for iOS WebKit browsers
   * @returns {Promise<Object>} User object or error
   */
  async signInWithGoogle() {
    try {
      const browserInfo = getBrowserDebugInfo();
      const authRecommendation = getRecommendedAuthMethod();

      console.log('🔐 Starting Google sign-in:', {
        browser: browserInfo.isIOSSafari ? 'iOS Safari' : browserInfo.isIOSWebKit ? 'iOS WebKit' : 'Other',
        recommendedMethod: authRecommendation.method,
        reason: authRecommendation.reason
      });

      // Log the exact flow being taken
      console.log('🔍 Auth flow decision:', {
        isIOSWebKit: isIOSWebKit(),
        recommendedMethod: authRecommendation.method,
        willUseRedirect: isIOSWebKit() || authRecommendation.method === 'redirect'
      });

      // iOS WebKit browsers (including Safari) should use redirect for better reliability
      if (isIOSWebKit()) {
        console.log('🍎 iOS WebKit detected - using redirect authentication for reliability');
        return await this.signInWithRedirectMethod();
      }

      // Use recommended method for other browsers
      if (authRecommendation.method === 'redirect') {
        console.log('📱 Using redirect authentication for mobile/restricted browser');
        return await this.signInWithRedirectMethod();
      } else {
        console.log('🖥️ Using popup authentication for desktop');
        return await this.signInWithPopupMethod();
      }
    } catch (error) {
      console.error('❌ Google sign-in error:', error);

      // Enhanced error logging with browser context
      console.error('Sign-in error details:', {
        code: error.code,
        message: error.message,
        browserInfo: getBrowserDebugInfo(),
        authRecommendation: getRecommendedAuthMethod()
      });

      return {
        success: false,
        error: this.getGoogleErrorMessage(error.code),
        errorType: 'firebase',
        originalError: error.code
      };
    }
  }

  /**
   * Safari-specific: Try popup first, fallback to redirect if storage issues
   * @param {Object} deviceInfo - Device detection information
   * @returns {Promise<Object>} Authentication result
   */
  async tryPopupThenRedirect(deviceInfo) {
    try {
      console.log('🍎 Attempting popup authentication on Safari mobile...');

      // Try popup first
      const result = await signInWithPopup(auth, this.googleProvider);
      const user = result.user;

      console.log('✅ Safari popup authentication successful:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });

      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        isNewUser: result._tokenResponse?.isNewUser || false,
        authMethod: 'popup-safari'
      };
    } catch (popupError) {
      console.warn('⚠️ Safari popup failed, trying redirect fallback:', popupError.code);

      // If popup fails due to storage partitioning or other Safari issues, try redirect
      if (popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/cancelled-popup-request' ||
          popupError.message.includes('storage')) {

        console.log('🔄 Falling back to redirect authentication for Safari...');
        return await this.signInWithRedirectMethod();
      }

      // If it's a different error, throw it
      throw popupError;
    }
  }

  /**
   * Popup authentication method with fallback to redirect
   * @returns {Promise<Object>} Authentication result
   */
  async signInWithPopupMethod() {
    try {
      console.log('🖥️ Attempting popup authentication...');
      console.log('🔍 Popup method details:', {
        authDomain: auth.config.authDomain,
        hasGoogleProvider: !!this.googleProvider,
        providerScopes: this.googleProvider._scopes
      });

      const result = await signInWithPopup(auth, this.googleProvider);
      const user = result.user;

      console.log('✅ Popup authentication successful:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });

      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        isNewUser: result._tokenResponse?.isNewUser || false,
        authMethod: 'popup'
      };
    } catch (popupError) {
      console.warn('⚠️ Popup authentication failed:', {
        code: popupError.code,
        message: popupError.message,
        customData: popupError.customData
      });

      console.error('❌ Full popup error:', popupError);

      // Handle popup-related errors by falling back to redirect
      if (popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/cancelled-popup-request' ||
          popupError.message.includes('Cross-Origin-Opener-Policy') ||
          popupError.message.includes('window.closed')) {

        console.log('🔄 Popup blocked or restricted - falling back to redirect authentication');
        console.log('   This is normal for browsers with strict security policies');
        return await this.signInWithRedirectMethod();
      }

      // For other errors, throw them
      throw popupError;
    }
  }

  /**
   * Redirect authentication method
   * @returns {Promise<Object>} Authentication result
   */
  async signInWithRedirectMethod() {
    console.log('📱 Initiating redirect authentication...');
    console.log('🔍 Current auth state before redirect:', {
      hasCurrentUser: !!auth.currentUser,
      userEmail: auth.currentUser?.email,
      isAnonymous: auth.currentUser?.isAnonymous
    });
    console.log('🔍 Firebase auth config:', {
      authDomain: auth.config.authDomain,
      apiKey: auth.config.apiKey ? 'Present' : 'Missing',
      projectId: auth.config.projectId
    });
    console.log('🔍 Google provider config:', {
      providerId: this.googleProvider.providerId,
      scopes: this.googleProvider._scopes
    });

    // IMPORTANT: Firebase Auth SDK automatically determines the correct redirect URI:
    // - Local development: http://localhost:3000 (base URL)
    // - Firebase Hosting: https://[project-id].firebaseapp.com/__/auth/handler
    //
    // Make sure your Google Cloud Console OAuth 2.0 Client has these Authorized redirect URIs:
    // 1. http://localhost:3000 (for local development)
    // 2. http://localhost (fallback for local development)
    // 3. https://rabbitwordskater.firebaseapp.com/__/auth/handler (for production)
    console.log('⚠️ IMPORTANT: Verify Google Cloud Console OAuth configuration:');
    console.log('   https://console.cloud.google.com/apis/credentials?project=' + auth.config.projectId);
    console.log('   Required redirect URIs:');
    console.log('   - http://localhost:3000 (local dev)');
    console.log('   - https://' + auth.config.authDomain + '/__/auth/handler (production)');

    // Store a flag in sessionStorage to detect when we return from redirect
    try {
      sessionStorage.setItem('pendingGoogleRedirect', Date.now().toString());
      console.log('✅ Set pendingGoogleRedirect flag in sessionStorage');
    } catch (storageError) {
      console.warn('⚠️ Could not set sessionStorage flag:', storageError);
      console.warn('   This may happen in Safari private mode or with storage partitioning');
    }

    // CRITICAL FIX: Do NOT sign out before redirect!
    // Signing out clears the auth state that Firebase needs to complete the redirect flow
    // This was causing the redirect result to be null when returning from Google

    // Initiate the redirect
    console.log('🚀 Starting redirect to Google OAuth...');
    console.log('🚀 Firebase SDK will automatically use the correct redirect URI for this environment');

    try {
      await signInWithRedirect(auth, this.googleProvider);
      console.log('✅ signInWithRedirect() completed - page should redirect now');
    } catch (error) {
      console.error('❌ signInWithRedirect() failed:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        customData: error.customData,
        stack: error.stack
      });

      // Clear the pending flag if redirect failed
      try {
        sessionStorage.removeItem('pendingGoogleRedirect');
      } catch (e) {
        // Ignore
      }

      throw error;
    }

    // For redirect, we return a pending state since the page will reload
    // NOTE: This code may not execute if the redirect happens immediately
    console.log('⚠️ Still here after signInWithRedirect - redirect may be delayed');
    return {
      success: true,
      pending: true,
      message: 'Redirecting to Google sign-in...',
      authMethod: 'redirect'
    };
  }

  /**
   * Sign out the current user
   * @returns {Promise<Object>} Success status or error
   */
  async logout() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: 'Failed to sign out. Please try again.'
      };
    }
  }

  /**
   * Get the current authenticated user
   * @returns {Object|null} Current user or null
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Listen for authentication state changes
   * @param {Function} callback - Callback function to handle auth state changes
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   * @param {string} errorCode - Firebase error code
   * @returns {string} User-friendly error message
   */
  getErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  /**
   * Convert Google OAuth error codes to user-friendly messages
   * Enhanced with Safari-specific error handling
   * @param {string} errorCode - Firebase error code
   * @returns {string} User-friendly error message
   */
  getGoogleErrorMessage(errorCode) {
    const deviceInfo = this.getDeviceDebugInfo();

    switch (errorCode) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/popup-blocked':
        if (deviceInfo.isSafariMobile) {
          return 'Safari blocked the sign-in popup. Please try again or use a different browser.';
        }
        return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email address but different sign-in credentials.';
      case 'auth/auth-domain-config-required':
        return 'Google sign-in is not properly configured. Please contact support.';
      case 'auth/operation-not-allowed':
        return 'Google sign-in is not enabled. Please contact support.';
      case 'auth/network-request-failed':
        if (deviceInfo.isSafariMobile) {
          return 'Network error on Safari. Please check your connection and try again.';
        }
        return 'Network error. Please check your connection and try again.';
      case 'auth/redirect-cancelled-by-user':
        return 'Sign-in was cancelled during redirect. Please try again.';
      case 'auth/redirect-operation-pending':
        return 'A sign-in redirect is already in progress. Please wait.';
      case 'auth/timeout':
        if (deviceInfo.isSafariMobile) {
          return 'Safari sign-in timed out. Please try again.';
        }
        return 'Sign-in timed out. Please try again.';
      case 'auth/web-storage-unsupported':
        return 'Your browser does not support web storage. Please enable cookies and try again.';
      case 'auth/invalid-api-key':
        return 'Invalid API configuration. Please contact support.';
      case 'auth/app-deleted':
        return 'Authentication service is unavailable. Please contact support.';
      default:
        if (deviceInfo.isSafariMobile) {
          return 'Safari sign-in failed. Please try again or use a different browser.';
        }
        return 'Google sign-in failed. Please try again.';
    }
  }

  /**
   * Test authentication configuration and provide debugging information
   * @returns {Object} Configuration test results
   */
  async testAuthConfiguration() {
    const deviceInfo = this.getDeviceDebugInfo();
    const testResults = {
      timestamp: new Date().toISOString(),
      deviceInfo,
      authDomain: auth.config.authDomain,
      apiKey: auth.config.apiKey ? 'Present' : 'Missing',
      projectId: auth.config.projectId,
      tests: {}
    };

    try {
      // Test 1: Check if auth is properly initialized
      testResults.tests.authInitialized = {
        passed: !!auth,
        message: auth ? 'Firebase Auth initialized' : 'Firebase Auth not initialized'
      };

      // Test 2: Check Google provider configuration
      testResults.tests.googleProvider = {
        passed: !!this.googleProvider,
        message: this.googleProvider ? 'Google provider configured' : 'Google provider not configured'
      };

      // Test 3: Check if we're in the right environment for the auth method
      const shouldUseRedirect = this.isMobileOrPopupBlocked();
      testResults.tests.authMethod = {
        passed: true,
        method: shouldUseRedirect ? 'redirect' : 'popup',
        message: `Will use ${shouldUseRedirect ? 'redirect' : 'popup'} authentication`
      };

      // Test 4: Check for any pending redirect results
      try {
        const redirectResult = await getRedirectResult(auth);
        testResults.tests.redirectResult = {
          passed: true,
          hasResult: !!redirectResult,
          message: redirectResult ? 'Redirect result available' : 'No redirect result pending'
        };
      } catch (redirectError) {
        testResults.tests.redirectResult = {
          passed: false,
          error: redirectError.code,
          message: `Redirect result error: ${redirectError.message}`
        };
      }

      console.log('🔧 Authentication Configuration Test Results:', testResults);
      return testResults;

    } catch (error) {
      testResults.tests.configurationError = {
        passed: false,
        error: error.code || error.message,
        message: `Configuration test failed: ${error.message}`
      };

      console.error('❌ Authentication configuration test failed:', error);
      return testResults;
    }
  }
}

// Export a singleton instance
const firebaseAuth = new FirebaseAuthService();

// Add global debugging method for development
if (typeof window !== 'undefined') {
  window.testFirebaseAuth = () => firebaseAuth.testAuthConfiguration();
  window.getAuthDebugInfo = () => firebaseAuth.getDeviceDebugInfo();
  console.log('🔧 Firebase Auth debugging available: window.testFirebaseAuth() and window.getAuthDebugInfo()');
}

export default firebaseAuth;
